export interface Service {
  image: string
  [key: string]: unknown
}

// Tiles are rendered server-side now; this serves one consumer, the client-side
// fuzzy index over every service name.
export const fetchAllServices = async (): Promise<Service[]> => {
  let allServices: Service[] = []

  const cacheBaseName = "recommended-services-cache"
  const cacheName = `${cacheBaseName}-${new Date().toISOString().slice(0, 10)}`
  const requestUrl = "/explore/services.json"

  // A stale session cookie makes this public endpoint 302 to /login, and fetch
  // follows it to a 200 HTML page. That is not an error, just nothing to index.
  const isJsonResponse = (response: Response) =>
    response.ok && (response.headers.get("content-type") ?? "").includes("application/json")

  const readServices = async (response: Response): Promise<boolean> => {
    if (!isJsonResponse(response)) return false

    allServices = await response.json()
    return true
  }

  const fetchWithFallback = async (): Promise<void> => {
    // The Cache API is best-effort: it can be unavailable (e.g. Safari private
    // mode throws on caches.open). Isolate it in its own try so a cache failure
    // falls through to a plain fetch — WITHOUT swallowing a later network error.
    let cache: Cache | undefined
    try {
      // Best-effort cache cleanup: swallow rejections so a failing delete/keys
      // call never surfaces as an unhandled rejection outside this try.
      void caches.delete(cacheBaseName).catch(() => {})
      void caches
        .keys()
        .then(keys =>
          keys
            .filter(key => key.startsWith(cacheBaseName) && key !== cacheName)
            .forEach(key => void caches.delete(key).catch(() => {})),
        )
        .catch(() => {})

      cache = await caches.open(cacheName)
      const cachedResponse = await cache.match(requestUrl)
      if (cachedResponse && (await readServices(cachedResponse))) return
    } catch {
      // Cache API unavailable — fall through to a direct fetch.
      cache = undefined
    }

    // Exactly one network request. A failure here (commonly a fetch aborted by
    // navigation on short-lived pages) is not retried: the retry would repeat a
    // request that is already doomed and just double the work. The rejection
    // propagates to the caller, which reports it.
    const networkResponse = await fetch(requestUrl)
    if (cache && isJsonResponse(networkResponse)) {
      // Best-effort cache write; a failure here must not block rendering.
      void cache.put(requestUrl, networkResponse.clone()).catch(() => {})
    }
    await readServices(networkResponse)
  }

  try {
    await fetchWithFallback()
  } catch (fallbackError) {
    window.App?.Sentry?.captureException(fallbackError, {
      tags: {
        component: "RecommendedServices",
        action: "fetch",
      },
    })
  }

  return allServices
}
